import { useFormikContext } from "formik";
import React, { useState } from "react";
import { AlertCircle, Eye, EyeOff, Info } from "lucide-react";
import { CustomSelect, SelectOption } from "./CustomSelect";
import { cn } from "@/utils/cn";

interface FormFieldProps {
  name: string;
  label?: string | React.ReactNode;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'checkbox' | 'radio' | 'date';
  placeholder?: string;
  disabled?: boolean;
  as?: 'input' | 'textarea' | 'select';
  rows?: number;
  children?: React.ReactNode;
  className?: string; // Corrected: added className
  inputClassName?: string;
  labelClassName?: string;
  helperText?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  options?: SelectOption[];
  searchable?: boolean;
  required?: boolean;
  min?: string | number;
  max?: string | number;
  testId?: string;
  passwordToggleTestId?: string;
  maxLength?: number;
  value?: any;
  onChange?: any;
  onBlur?: any;
  error?: string | boolean;
  selectTopSlot?: React.ReactNode;
  autoFocus?: boolean;
}

// Reusable FormField Component
const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  type = 'text',
  placeholder,
  disabled = false,
  as = 'input',
  rows = 3,
  children,
  className = '',
  inputClassName = '',
  labelClassName = '',
  onKeyDown,
  helperText,
  options,
  searchable,
  required = false,
  min,
  max,
  testId,
  passwordToggleTestId,
  maxLength,
  value,
  onChange,
  onBlur,
  error,
  selectTopSlot,
  autoFocus
}) => {
  const formik = useFormikContext<any>();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const fieldProps = formik ? formik.getFieldProps(name) : { name, value, onChange, onBlur };
  const meta = formik ? formik.getFieldMeta(name) : { touched: !!error, error };
  const hasError: boolean = Boolean(meta.touched && meta.error);

  const inputType: string = type === 'password' && showPassword ? 'text' : type;

  const baseClasses = `w-full px-4 py-2 border rounded-sm shadow-none focus:outline-none focus:ring-1 focus:ring-ui-primary/20 transition-all placeholder:text-ui-muted text-sm ${hasError
    ? 'border-ui-error focus:ring-ui-error/20 focus:border-ui-error'
    : 'border-ui-hairline focus:border-ui-primary'
    } ${disabled ? 'bg-ui-surface-soft text-ui-muted border-ui-hairline cursor-not-allowed' : 'bg-ui-surface-card text-ui-ink'} ${inputClassName}`;

  if (type === 'checkbox' || type === 'radio') {
    return (
      <div className={`mb-1 ${className}`}>
        <label className="flex items-center space-x-2 cursor-pointer group">
          <div className="relative flex items-center">
            <input
              {...fieldProps}
              id={name}
              type={type}
              data-testid={testId}
              disabled={disabled}
              checked={fieldProps.value}
              autoComplete="off"
              min={min}
              max={max}
              className={`w-4 h-4 transition-all text-ui-primary border-ui-hairline rounded-sm focus:ring-ui-primary ${disabled ? 'bg-ui-surface-soft cursor-not-allowed' : 'bg-ui-surface-card cursor-pointer'
                }`}
            />
          </div>
          {label && (
            <span className={`text-xs font-semibold text-ui-body ${labelClassName}`}>
              {label}
            </span>
          )}
        </label>
        {hasError && (
          <div className="flex items-center mt-1 text-ui-error text-xs">
            <AlertCircle size={14} className="mr-1" />
            {meta.error}
          </div>
        )}
      </div>
    );
  }

  const renderInput = () => {
    switch (as) {
      case 'textarea':
        return (
          <div>
            <textarea
              {...fieldProps}
              id={name}
              data-testid={testId}
              placeholder={placeholder}
              disabled={disabled}
              rows={rows}
              maxLength={maxLength}
              onKeyDown={onKeyDown}
              className={`${baseClasses} resize-none`}
            />
            {helperText && (
              <p className="text-[10px] text-ui-muted mt-1 flex items-center"><Info size={10} className="mr-1 text-ui-muted" />{helperText}</p>
            )}
          </div>
        );
      case 'select':
        const selectOptions: SelectOption[] = options || [];

        // If no options array provided, try to extract from children (options)
        if (selectOptions.length === 0 && children) {
          React.Children.forEach(children, (child) => {
            if (React.isValidElement(child) && (child.type === 'option' || (child.type as any).displayName === 'option')) {
              const props = child.props as any;
              selectOptions.push({
                value: props.value?.toString() || '',
                label: props.children?.toString() || '',
              });
            }
          });
        }

        return (
          <CustomSelect
            options={selectOptions}
            value={fieldProps.value}
            onChange={(val) => formik ? formik.setFieldValue(name, val) : onChange?.({ target: { name, value: val } })}
            disabled={disabled}
            searchable={searchable}
            className={hasError ? 'border-ui-error rounded-sm ring-1 ring-ui-error' : ''}
            placeholder={placeholder}
            topSlot={selectTopSlot}
          />
        );
      default:
        return (
          <div>
            <div className="relative">
              <input
                {...fieldProps}
                onChange={(e) => {
                  if (onChange) {
                    onChange(e);
                  } else {
                    fieldProps.onChange(e);
                  }
                }}
                onBlur={(e) => {
                  if (onBlur) {
                    onBlur(e);
                  } else {
                    fieldProps.onBlur(e);
                  }
                }}
                id={name}
                type={inputType}
                data-testid={testId}
                placeholder={placeholder}
                disabled={disabled}
                onKeyDown={onKeyDown}
                min={min}
                max={max}
                autoFocus={autoFocus}
                maxLength={maxLength}
                className={`${baseClasses} ${type === 'password' ? 'pr-9' : ''}`}
              />

              {type === 'password' && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ui-muted hover:text-ui-body"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid={passwordToggleTestId}
                >
                  {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              )}
            </div>
            {helperText && (
              <p className="text-[10px] text-ui-muted mt-1 flex items-center"><Info size={10} className="mr-1 text-ui-muted" />{helperText}</p>
            )}
          </div>
        );
    }
  };

  return (
    <div className={cn("mb-3", className)}>
      {label && (
        <label
          htmlFor={name}
          className={` text-xs font-semibold text-ui-body mb-1 flex ${labelClassName}`}
        >
          {label}
          {required && <span className="text-ui-error ml-1">*</span>}
        </label>
      )}

      {renderInput()}

      {hasError && (
        <div className="flex items-center mt-1 text-ui-error text-xs">
          {/* <AlertCircle size={12} className="mr-1" /> */}
          {meta.error}
        </div>
      )}
    </div>
  );
};

export default FormField;
